import { AlertCircle, Check, CheckCircle2, Clock, Copy, Languages, Lightbulb, RefreshCw, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { checkGrammar, translateJaToEn } from '@/api/api'
import { GrammarCheckResponse, TranslateJaToEnResponse } from '@/api/type'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { formatRelativeTime } from '@/lib/format-utils'

type TranslationHistory = {
  id: string
  input: string
  result: TranslateJaToEnResponse
  timestamp: number
}

const TRANSLATION_HISTORY_KEY = 'translation_history'
const MAX_HISTORY_COUNT = 10

export const GrammarCheck = () => {
  const [inputText, setInputText] = useState<string>('')
  const [isChecking, setIsChecking] = useState<boolean>(false)
  const [result, setResult] = useState<GrammarCheckResponse | null>(null)
  const [activeTab, setActiveTab] = useState<'grammar' | 'translation'>('grammar')

  // 翻訳用の状態
  const [translationInput, setTranslationInput] = useState<string>('')
  const [isTranslating, setIsTranslating] = useState<boolean>(false)
  const [translationResult, setTranslationResult] = useState<TranslateJaToEnResponse | null>(null)
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)
  const [translationHistory, setTranslationHistory] = useState<TranslationHistory[]>([])
  const [showHistory, setShowHistory] = useState<boolean>(false)

  // 履歴の読み込み
  useEffect(() => {
    try {
      const stored = localStorage.getItem(TRANSLATION_HISTORY_KEY)
      if (stored) {
        setTranslationHistory(JSON.parse(stored))
      }
    } catch (error) {
      console.error('Failed to load translation history:', error)
    }
  }, [])

  // 履歴を保存
  const saveHistory = useCallback((newHistory: TranslationHistory[]) => {
    try {
      localStorage.setItem(TRANSLATION_HISTORY_KEY, JSON.stringify(newHistory))
      setTranslationHistory(newHistory)
    } catch (error) {
      console.error('Failed to save translation history:', error)
    }
  }, [])

  // 履歴に追加
  const addToHistory = useCallback(
    (input: string, result: TranslateJaToEnResponse) => {
      const newItem: TranslationHistory = {
        id: Date.now().toString(),
        input,
        result,
        timestamp: Date.now(),
      }
      const newHistory = [newItem, ...translationHistory].slice(0, MAX_HISTORY_COUNT)
      saveHistory(newHistory)
    },
    [translationHistory, saveHistory],
  )

  // 履歴から削除
  const removeFromHistory = useCallback(
    (id: string) => {
      const newHistory = translationHistory.filter((item) => item.id !== id)
      saveHistory(newHistory)
      toast.success('履歴を削除しました')
    },
    [translationHistory, saveHistory],
  )

  // 履歴から選択
  const selectFromHistory = useCallback((item: TranslationHistory) => {
    setTranslationInput(item.input)
    setTranslationResult(item.result)
    setShowHistory(false)
    setCopiedIndex(null)
  }, [])

  // グラマーチェック実行
  const handleCheck = useCallback(async () => {
    if (!inputText.trim()) return

    setIsChecking(true)
    try {
      const response = await checkGrammar({ text: inputText })
      setResult(response)
      toast.success('文法チェックが完了しました')
    } catch (error) {
      console.error('Grammar check failed:', error)
      toast.error('文法チェックに失敗しました')
    } finally {
      setIsChecking(false)
    }
  }, [inputText])

  // 翻訳実行
  const handleTranslate = useCallback(async () => {
    if (!translationInput.trim()) return

    setIsTranslating(true)
    try {
      const response = await translateJaToEn({ text: translationInput })
      setTranslationResult(response)
      setCopiedIndex(null) // 翻訳結果が変わったらコピー状態をリセット
      addToHistory(translationInput, response) // 履歴に追加
      if (!response.is_japanese) {
        toast.warning('入力されたテキストは日本語ではない可能性があります')
      } else {
        toast.success('翻訳が完了しました')
      }
    } catch (error) {
      console.error('Translation failed:', error)
      toast.error('翻訳に失敗しました')
    } finally {
      setIsTranslating(false)
    }
  }, [translationInput, addToHistory])
  // クリップボードにコピー
  const handleCopy = useCallback(async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedIndex(index)
      toast.success('クリップボードにコピーしました')
      // 2秒後にチェックマークを消す
      setTimeout(() => setCopiedIndex(null), 2000)
    } catch (error) {
      console.error('Copy failed:', error)
      toast.error('コピーに失敗しました')
    }
  }, [])

  // エラー重要度の表示設定
  const getSeverityColor = useCallback((severity: 'critical' | 'major' | 'minor') => {
    switch (severity) {
      case 'critical':
        return 'destructive'
      case 'major':
        return 'default'
      case 'minor':
        return 'secondary'
    }
  }, [])

  // フォーマルレベルの表示設定
  const getFormalityColor = useCallback((level: 'casual' | 'neutral' | 'formal' | 'very_formal') => {
    switch (level) {
      case 'casual':
        return 'secondary'
      case 'neutral':
        return 'default'
      case 'formal':
      case 'very_formal':
        return 'outline'
    }
  }, [])

  // ペルソナごとのスタイル設定
  const getPersonaStyles = useCallback((persona: 'general' | 'casual' | 'engineer') => {
    switch (persona) {
      case 'general':
        return {
          borderColor: 'border-l-blue-500',
          bgColor: 'bg-blue-500/5',
          badgeVariant: 'default' as const,
          label: '一般',
        }
      case 'casual':
        return {
          borderColor: 'border-l-green-500',
          bgColor: 'bg-green-500/5',
          badgeVariant: 'secondary' as const,
          label: 'カジュアル',
        }
      case 'engineer':
        return {
          borderColor: 'border-l-purple-500',
          bgColor: 'bg-purple-500/5',
          badgeVariant: 'outline' as const,
          label: 'エンジニア',
        }
    }
  }, [])

  // 総合評価のアイコン
  const AssessmentIcon = useMemo(() => {
    if (!result) return null
    return result.is_perfect ? (
      <CheckCircle2 className="size-5 text-green-500" />
    ) : (
      <AlertCircle className="size-5 text-yellow-500" />
    )
  }, [result])

  return (
    <div className="flex h-full flex-col">
      {/* タブ切り替え */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'grammar' | 'translation')}>
        <TabsList className="grid w-full grid-cols-2 bg-transparent p-0">
          <TabsTrigger
            value="grammar"
            className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-3 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
          >
            文法チェック
          </TabsTrigger>
          <TabsTrigger
            value="translation"
            className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-3 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
          >
            翻訳
          </TabsTrigger>
        </TabsList>

        {/* 文法チェックタブ */}
        <TabsContent value="grammar" className="flex flex-1 flex-col overflow-hidden">
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
            {/* 入力エリア */}
            <div className="flex flex-col gap-2">
              <label htmlFor="input-text" className="text-sm font-medium">
                チェックする英文
              </label>
              <textarea
                id="input-text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="英文を入力してください..."
                className="min-h-32 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            {/* チェックボタン */}
            <Button onClick={handleCheck} disabled={!inputText.trim() || isChecking} className="w-full">
              {isChecking ? (
                <>
                  <RefreshCw className="mr-2 size-4 animate-spin" />
                  チェック中...
                </>
              ) : (
                'チェック'
              )}
            </Button>

            {/* 結果表示 */}
            {result && (
              <div className="flex flex-col gap-4">
                <Separator />

                {/* 文章の意味 */}
                <div className="rounded-md border p-4">
                  <h3 className="mb-2 font-semibold">文章の意味</h3>
                  <p className="text-sm text-muted-foreground">{result.interpreted_meaning}</p>
                </div>

                {/* 総合評価 */}
                <div className="flex items-start gap-3 rounded-md border p-4">
                  {AssessmentIcon}
                  <div className="flex-1">
                    <h3 className="mb-2 font-semibold">総合評価</h3>
                    <p className="text-sm text-muted-foreground">{result.overall_assessment}</p>
                  </div>
                </div>

                {/* 修正後のテキスト */}
                {!result.is_perfect && (
                  <div className="rounded-md border p-4">
                    <h3 className="mb-2 font-semibold">修正後のテキスト</h3>
                    <p className="text-sm">{result.corrected_text}</p>
                  </div>
                )}

                {/* 優先的に修正すべきエラー */}
                {result.priority_fixes.length > 0 && (
                  <div className="rounded-md border border-yellow-500/50 bg-yellow-500/10 p-4">
                    <h3 className="mb-2 flex items-center gap-2 font-semibold">
                      <AlertCircle className="size-4" />
                      優先的に修正すべき点
                    </h3>
                    <ul className="space-y-1">
                      {result.priority_fixes.map((fix, index) => (
                        <li key={index} className="text-sm text-muted-foreground">
                          • {fix}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* エラー詳細 */}
                {result.errors.length > 0 && (
                  <div className="rounded-md border p-4">
                    <h3 className="mb-3 font-semibold">文法エラー ({result.errors.length}件)</h3>
                    <div className="space-y-3">
                      {result.errors.map((error, index) => (
                        <div key={index} className="rounded-md border p-3">
                          <div className="mb-2 flex items-center gap-2">
                            <Badge variant={getSeverityColor(error.severity)}>{error.severity}</Badge>
                            <span className="text-xs text-muted-foreground">{error.category}</span>
                          </div>
                          <p className="mb-1 text-sm font-medium">
                            誤: <span className="text-red-500">{error.position.word}</span> → 正:{' '}
                            <span className="text-green-500">{error.correction}</span>
                          </p>
                          <p className="text-sm text-muted-foreground">{error.explanation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 改善提案 */}
                {result.suggestions.length > 0 && (
                  <div className="rounded-md border p-4">
                    <h3 className="mb-3 flex items-center gap-2 font-semibold">
                      <Lightbulb className="size-4" />
                      改善提案
                    </h3>
                    <div className="space-y-3">
                      {result.suggestions.map((suggestion, index) => (
                        <div key={index} className="rounded-md border p-3">
                          <p className="mb-2 text-sm">
                            <span className="font-medium">元:</span> {suggestion.original}
                          </p>
                          <p className="mb-1 text-sm">
                            <span className="font-medium">代替案:</span>
                          </p>
                          <div className="mb-2 flex flex-wrap gap-2">
                            {suggestion.alternatives.map((alt, altIndex) => (
                              <Badge key={altIndex} variant="outline">
                                {alt}
                              </Badge>
                            ))}
                          </div>
                          <p className="text-xs text-muted-foreground">{suggestion.reason}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 代替バージョン */}
                {result.alternative_versions.length > 0 && (
                  <div className="rounded-md border p-4">
                    <h3 className="mb-3 font-semibold">別の表現方法</h3>
                    <div className="space-y-3">
                      {result.alternative_versions.map((version, index) => (
                        <div key={index} className="rounded-md border p-3">
                          <div className="mb-2 flex items-center gap-2">
                            <Badge variant={getFormalityColor(version.formality_level)}>
                              {version.formality_level}
                            </Badge>
                          </div>
                          <p className="mb-2 text-sm font-medium">{version.text}</p>
                          <p className="mb-1 text-sm text-muted-foreground">{version.nuance_difference}</p>
                          <p className="text-xs text-muted-foreground">
                            <span className="font-medium">適した場面:</span> {version.use_case}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        {/* 翻訳タブ */}
        <TabsContent value="translation" className="flex flex-1 flex-col overflow-hidden">
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
            {/* 入力エリア */}
            <div className="flex flex-col gap-2">
              <label htmlFor="translation-input" className="text-sm font-medium">
                日本語を入力
              </label>
              <textarea
                id="translation-input"
                value={translationInput}
                onChange={(e) => setTranslationInput(e.target.value)}
                placeholder="翻訳したい日本語を入力してください..."
                className="min-h-32 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            {/* 翻訳ボタンと履歴ボタン */}
            <div className="flex gap-2">
              <Button onClick={handleTranslate} disabled={!translationInput.trim() || isTranslating} className="flex-1">
                {isTranslating ? (
                  <>
                    <RefreshCw className="mr-2 size-4 animate-spin" />
                    翻訳中...
                  </>
                ) : (
                  <>
                    <Languages className="mr-2 size-4" />
                    翻訳
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowHistory(!showHistory)}
                disabled={translationHistory.length === 0}
                className="px-3"
              >
                <Clock className="size-4" />
                {translationHistory.length > 0 && <span className="ml-1 text-xs">({translationHistory.length})</span>}
              </Button>
            </div>

            {/* 履歴表示 */}
            {showHistory && translationHistory.length > 0 && (
              <div className="rounded-md border">
                <div className="border-b p-3">
                  <h3 className="flex items-center gap-2 text-sm font-semibold">
                    <Clock className="size-4" />
                    翻訳履歴 (最新{translationHistory.length}件)
                  </h3>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {translationHistory.map((item) => (
                    <div
                      key={item.id}
                      className="group flex items-start gap-2 border-b p-3 last:border-b-0 hover:bg-muted/50"
                    >
                      <button onClick={() => selectFromHistory(item)} className="flex-1 text-left px-2 py-1">
                        <p className="mb-1 text-sm">{item.input}</p>
                        <p className="text-xs text-muted-foreground">{formatRelativeTime(item.timestamp)}</p>
                      </button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFromHistory(item.id)}
                        className="h-8 px-2 opacity-0 group-hover:opacity-100"
                      >
                        <Trash2 className="size-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 結果表示 */}
            {translationResult && (
              <div className="flex flex-col gap-4">
                <Separator />

                {/* 日本語判定の警告 */}
                {!translationResult.is_japanese && (
                  <div className="rounded-md border border-yellow-500/50 bg-yellow-500/10 p-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="size-5 text-yellow-500" />
                      <p className="text-sm">入力されたテキストは日本語ではない可能性があります</p>
                    </div>
                  </div>
                )}

                {/* 注意事項 */}
                {translationResult.notes && (
                  <div className="rounded-md border p-4">
                    <h3 className="mb-2 flex items-center gap-2 font-semibold">
                      <Lightbulb className="size-4" />
                      注意事項
                    </h3>
                    <p className="text-sm text-muted-foreground">{translationResult.notes}</p>
                  </div>
                )}

                {/* 翻訳バリエーション */}
                <div className="flex flex-col gap-4">
                  {translationResult.variants.map((variant, index) => {
                    const styles = getPersonaStyles(variant.persona)
                    return (
                      <div
                        key={index}
                        className={`rounded-md border border-l-4 p-4 ${styles.borderColor} ${styles.bgColor}`}
                      >
                        <div className="mb-3 flex items-center gap-2">
                          <Badge variant={styles.badgeVariant}>{styles.label}</Badge>
                          <span className="text-xs text-muted-foreground">{variant.feature}</span>
                        </div>
                        <div className="mb-3">
                          <div className="mb-2 flex items-center justify-between">
                            <p className="text-xs font-medium text-muted-foreground">英訳</p>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2"
                              onClick={() => handleCopy(variant.translation, index)}
                            >
                              {copiedIndex === index ? (
                                <>
                                  <Check className="mr-1 size-3" />
                                  <span className="text-xs">コピー済み</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="mr-1 size-3" />
                                  <span className="text-xs">コピー</span>
                                </>
                              )}
                            </Button>
                          </div>
                          <p className="text-base font-medium">{variant.translation}</p>
                        </div>

                        {/* 逆翻訳 */}
                        <div className="mb-3">
                          <p className="mb-1 text-xs font-medium text-muted-foreground">逆翻訳（確認用）</p>
                          <p className="text-sm">{variant.back_translation}</p>
                        </div>

                        {/* ニュアンス */}
                        <div>
                          <p className="mb-1 text-xs font-medium text-muted-foreground">ニュアンス</p>
                          <p className="text-sm">{variant.nuance}</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
